import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button/Button';

interface GameAdsBannerProps {
  banner: {
    enabled: boolean;
    status: string;
    label: string;
  };
  onImpression: () => void;
  onDismiss: () => void;
}

const GameAdsBanner = ({ banner, onImpression, onDismiss }: GameAdsBannerProps) => {
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLElement>(null);
  const impressionReportedRef = useRef(false);

  useEffect(() => {
    const element = bannerRef.current;
    if (!banner.enabled || dismissed || impressionReportedRef.current || !element) return;

    const reportVisibleImpression = () => {
      if (impressionReportedRef.current) return;
      impressionReportedRef.current = true;
      onImpression();
    };

    // Older browsers without IntersectionObserver fall back to a rendered-slot impression.
    if (typeof IntersectionObserver === 'undefined') {
      reportVisibleImpression();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && entry.intersectionRatio > 0) {
        reportVisibleImpression();
        observer.disconnect();
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [banner.enabled, dismissed, onImpression]);

  if (!banner.enabled || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss();
  };

  return (
    <aside
      ref={bannerRef}
      className="bg-bg-subtle flex min-h-gameplay-control w-full items-center justify-between gap-3 rounded-lg border border-outline-default px-3 py-2"
      aria-label="Test advertisement"
      data-testid="game-ads-banner"
    >
      <div className="flex min-w-0 flex-col">
        <span className="text-micro font-semibold tracking-wide text-glyph-tertiary uppercase">
          {banner.label}
        </span>
        <span className="text-caption text-glyph-secondary">Mock banner placement</span>
      </div>
      <Button variant="ghost" size="md" onClick={handleDismiss}>
        Dismiss
      </Button>
    </aside>
  );
};

export default GameAdsBanner;
